import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchPromoCode } from '@/features/billing/billingThunks';
import { setCheckoutPromoCode } from '@/features/billing/billingSlice';
import {
  isSamePromoCode,
  normalizePromoCode,
  resolvePromoCodeCheck,
} from '@/lib/checkoutPromoCode';
import { InputLabel } from '../ui/InputLabel';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

type CheckoutPromoCodeProps = {
  workspaceId: number | undefined;
  activeCode: string | null;
  editable: boolean;
};

/**
 * Code promo du tunnel de premier paiement. Un code saisi ne remplace le code actif qu'une fois
 * validé, et rien n'est enregistré sur le workspace : le back le fait à la création de la souscription.
 */
export default function CheckoutPromoCode({
  workspaceId,
  activeCode,
  editable,
}: CheckoutPromoCodeProps) {
  const { t } = useTranslation('billing');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const isChecking = useAppSelector((s) => s.billing.promoCode.loading);

  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const pendingCheckRef = useRef<{ abort: () => void } | null>(null);

  const abortPendingCheck = () => {
    pendingCheckRef.current?.abort();
    pendingCheckRef.current = null;
  };

  useEffect(() => {
    const pendingCheck = pendingCheckRef;
    return () => {
      pendingCheck.current?.abort();
      pendingCheck.current = null;
    };
  }, []);

  const closeEditor = () => {
    abortPendingCheck();
    setIsEditing(false);
    setInputValue('');
    setError(null);
  };

  const handleApply = async () => {
    const code = normalizePromoCode(inputValue);
    if (!code || workspaceId == null) return;
    if (isSamePromoCode(code, activeCode)) {
      closeEditor();
      return;
    }

    setError(null);
    abortPendingCheck();
    const request = dispatch(fetchPromoCode({ workspaceId, code }));
    pendingCheckRef.current = request;
    const result = await request;
    // Saisie modifiée, nouvelle validation ou démontage entre-temps : la réponse ne vaut plus rien.
    if (pendingCheckRef.current !== request) return;
    pendingCheckRef.current = null;

    const check = resolvePromoCodeCheck(
      fetchPromoCode.fulfilled.match(result) ? result.payload : null,
      fetchPromoCode.rejected.match(result) ? (result.payload ?? null) : null,
    );

    if (check.kind === 'accepted') {
      // Les tarifs sont relus par UsageExpiredAlert, qui suit le code du tunnel.
      dispatch(setCheckoutPromoCode(check.code));
      closeEditor();
      return;
    }

    if (check.kind === 'notFound') setError(t('promoCode.errorNotFound'));
    else if (check.kind === 'inactive') setError(t('promoCode.errorInvalid'));
    else setError(tCommon(check.message ?? 'errors.generic'));
  };

  const showForm = editable && (!activeCode || isEditing);

  if (!activeCode && !showForm) return null;

  return (
    <div className="mt-6 rounded-lg space-y-4">
      {activeCode && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-600 mb-1">{t('promoCode.applied')}</p>
            <p className="text-base font-mono font-medium text-gray-800 select-none truncate">
              {activeCode}
            </p>
          </div>
          {editable && !isEditing && (
            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              {t('promoCode.edit')}
            </Button>
          )}
        </div>
      )}

      {showForm && (
        <div className="mb-4 space-y-4">
          <Label className="text-lg font-medium">
            {activeCode ? t('promoCode.replaceLabel') : t('promoCode.addLabel')}
          </Label>{' '}
          <div>
            <InputLabel
              label={t('promoCode.inputLabel')}
              type="text"
              value={inputValue}
              onChange={(e) => {
                abortPendingCheck();
                setInputValue(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!isChecking) void handleApply();
                }
              }}
            />
            {error && <p className="text-xs text-error mt-2">{error}</p>}
          </div>
          <div className="flex gap-2">
            {activeCode && (
              <Button type="button" variant="outline" className="flex-1" onClick={closeEditor}>
                {t('promoCode.cancel')}
              </Button>
            )}
            <Button
              type="button"
              className="flex-1"
              onClick={() => void handleApply()}
              disabled={isChecking || !inputValue.trim() || workspaceId == null}
            >
              {isChecking ? t('promoCode.verifying') : t('promoCode.apply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
